import {
  Box,
  CircularProgress,
  Collapse,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
} from "@mui/material";
import { RxCross1 } from "react-icons/rx";
import { useCallback, useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  fetchUpdateProduct,
  getPlatform,
  updateStocksPrice,
} from "../../../../services/InventoryAPI";
import { useFormik } from "formik";
import { IGNORE_TYPE } from "../../../constants/constant";
import { FaChevronDown, FaChevronUp, FaTrash } from "react-icons/fa";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 600,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
};

const toIDR = (val) =>
  val?.toLocaleString("id-ID", {
    style: "currency",
    currency: "IDR",
  });

const ModalChangePrice = ({ open, setOpen, dataPo, refetch, isWarehouse }) => {
  const [type, setType] = useState([]);
  const [collapseArr, setCollapseArr] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");

  const handleClose = () => {
    setErrorMessage("");
    setOpen(false);
  };

  const {
    mutate: mutateProduct,
    data: dataProduct,
    isLoading: isLoadingProduct,
  } = useMutation({
    mutationFn: fetchUpdateProduct,
  });

  const pharmacyNetPriceFinal = (
    discountPercent,
    pharmacyNetPriceReal,
    taxValue
  ) => {
    if (!pharmacyNetPriceReal) return 0;
    let discount =
      pharmacyNetPriceReal * (discountPercent ? discountPercent / 100 : 0);
    let value =
      getDppPrice(discountPercent, pharmacyNetPriceReal, taxValue) * taxValue;

    return pharmacyNetPriceReal - discount + value;
  };

  const quantityValue = (index, count) => {
    if (type[index]?.type === dataProduct[index]?.packaging?.type_unit) {
      return count;
    }
    if (type[index]?.type === dataProduct[index]?.packaging?.type_strip) {
      if (IGNORE_TYPE.includes(dataProduct[index]?.packaging?.type_unit)) {
        return count;
      } else {
        return count * dataProduct[index]?.packaging?.total_unit;
      }
    }
    if (type[index]?.type === dataProduct[index]?.packaging?.type_box) {
      if (IGNORE_TYPE.includes(dataProduct[index]?.packaging?.type_unit)) {
        return count * dataProduct[index]?.packaging?.total_strip;
      } else {
        return (
          count *
          dataProduct[index]?.packaging?.total_strip *
          dataProduct[index]?.packaging?.total_unit
        );
      }
    }
  };

  const getDppPrice = (discountPercent, pharmacyNetPriceReal, taxValue) => {
    if (!pharmacyNetPriceReal) return 0;
    let discount =
      pharmacyNetPriceReal * (discountPercent ? discountPercent / 100 : 0);
    let tax = taxValue ? taxValue * 100 : 0;

    return (
      (pharmacyNetPriceReal - discount) *
      (tax === 12 ? 11 / 12 : tax === 11 ? 1 : 0)
    );
  };

  const formik = useFormik({
    initialValues: {
      warehouse: isWarehouse ? isWarehouse : false,
      product: dataProduct
        ? dataProduct.map((data) => ({
            id: data.product_id,
            name_product: data.product_name,
            sell_price: data.sell_price,
            sell_price_sekunder: data.sell_price_sekunder,
            sell_price_tersier: data.sell_price_tersier,
            pharmacy_net_price: 0,
            pharmacy_net_price_discount: data.pharmacy_net_price_discount,
            pharmacy_net_price_ppn: data.pharmacy_net_price_ppn,
            pharmacy_net_price_ppn_value: data.pharmacy_net_price_ppn_value
              ? data.pharmacy_net_price_ppn_value
              : 0,
            dpp: data.dpp ? data.dpp : 0,
            pharmacy_net_price_real: data.pharmacy_net_price_real,
            platform_price:
              data.platform !== null
                ? data.platform.map((p) => ({
                    platform_id: p.platform_id,
                    name: p.name,
                    price: p.price,
                    is_active: p.is_active,
                  }))
                : [],
            price_grosir:
              data.price_grosir !== null
                ? data.price_grosir.map((pg) => ({
                    minimum: pg.minimum,
                    maximum: pg.maximum,
                    price_grosirs: pg.price_grosirs,
                  }))
                : [],
          }))
        : [],
    },
    enableReinitialize: true,
    onSubmit: (values) => {
      mutateUpdateStock({
        warehouse: values.warehouse,
        product_update: values.product.map((data, index) => ({
          id: data.id,
          name_product: data.name_product,
          pharmacy_net_price: pharmacyNetPriceFinal(
            data.pharmacy_net_price_discount,
            data.pharmacy_net_price_real,
            data.pharmacy_net_price_ppn_value
          ),
          pharmacy_net_price_discount: data.pharmacy_net_price_discount,
          pharmacy_net_price_ppn: data.pharmacy_net_price_ppn,
          pharmacy_net_price_ppn_value: data.pharmacy_net_price_ppn_value,
          pharmacy_net_price_real: data.pharmacy_net_price_real,
          ppn: data.ppn,
          platform_price: data.platform_price,
          sell_price: data.sell_price,
          sell_price_sekunder: data.sell_price_sekunder,
          sell_price_tersier: data.sell_price_tersier,
          price_grosir: data.price_grosir,
        })),
      });
    },
  });

  const { data: platforms } = useQuery({
    queryKey: ["platform"],
    queryFn: () => getPlatform(),
    keepPreviousData: true,
  });

  const { mutate: mutateUpdateStock, isLoading: isLoadingUpdateStock } =
    useMutation({
      mutationFn: updateStocksPrice,
      onError: (err) => {
        setErrorMessage(err.message.id ? err.message.id : err.message);
      },
      onSuccess: () => {
        refetch();
        handleClose();
      },
    });

  const handleAddPlatform = useCallback(
    (data, index) => {
      const intersection = new Set(
        formik.values.product.map((p) =>
          p.platform_price.map((u) => u.platform_id)
        )[index]
      );
      const result = platforms?.filter(({ id }) => !intersection.has(id));

      formik.setFieldValue(`product[${index}]`, {
        ...formik.values.product[index],
        platform_price: [
          ...formik.values.product[index].platform_price,
          {
            platform_id: result[0]?.id,
            name: result[0]?.name,
            price: "",
            is_active: true,
          },
        ],
      });
    },
    [formik.values.product]
  );

  useEffect(() => {
    if (open && dataPo) {
      mutateProduct({
        id: dataPo.product.map((item) => item.product_id),
        warehouse: isWarehouse ? isWarehouse : false,
      });
    }
  }, [open]);

  useEffect(() => {
    if (dataProduct && !isLoadingProduct)
      setCollapseArr(Array(dataProduct.length).fill(true));
  }, [dataProduct]);

  return (
    <>
      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            ...style,
            maxWidth: "75%",
            overflowY: "auto",
            overflowX: "wrap",
            maxHeight: "80vh",
            padding: 0,
          }}
          className="text-left text-[12px] w-3/4 lg:w-1/3 h-full"
        >
          <form onSubmit={formik.handleSubmit}>
            <div className="flex justify-between items-center px-8 py-4">
              <p className="font-bold text-lg">Ubah Harga Jual</p>
              <RxCross1
                className="cursor-pointer hover:text-red-500"
                onClick={handleClose}
              />
            </div>

            <div className="px-8 mb-4 h-[55vh] overflow-y-auto">
              <div className="mb-4">
                {dataProduct && !isLoadingProduct ? (
                  <>
                    {formik.values.product.map((item, i) => {
                      return (
                        <div className="mb-2 rounded-md p-4 border">
                          <div
                            className="flex justify-between items-center cursor-pointer hover:text-gray-300"
                            onClick={() =>
                              setCollapseArr([
                                ...collapseArr.slice(0, i),
                                !collapseArr[i],
                                ...collapseArr.slice(i + 1),
                              ])
                            }
                          >
                            <p className="font-bold">{item.name_product}</p>
                            {collapseArr[i] ? (
                              <FaChevronUp />
                            ) : (
                              <FaChevronDown />
                            )}
                          </div>
                          <Collapse
                            in={collapseArr[i]}
                            timeout="auto"
                            unmountOnExit
                          >
                            <div className="mt-4">
                              <TextField
                                id="outlined-basic"
                                fullWidth
                                label="Harga Jual Primer"
                                autoComplete="off"
                                variant="outlined"
                                value={item.sell_price}
                                onChange={(e) => {
                                  let numericValue = e.target.value;
                                  if (numericValue === "") {
                                    numericValue = "0";
                                  }
                                  formik.setFieldValue(`product[${i}]`, {
                                    ...item,
                                    sell_price: parseInt(
                                      numericValue.replace(/[^0-9]/g, ""),
                                      10
                                    ),
                                  });
                                }}
                                sx={{ marginBottom: 2 }}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      Rp.
                                    </InputAdornment>
                                  ),
                                }}
                              />
                              <TextField
                                id="outlined-basic"
                                fullWidth
                                label="Harga Jual Sekunder"
                                autoComplete="off"
                                variant="outlined"
                                value={item.sell_price_sekunder}
                                onChange={(e) => {
                                  let numericValue = e.target.value;
                                  if (numericValue === "") {
                                    numericValue = "0";
                                  }
                                  formik.setFieldValue(`product[${i}]`, {
                                    ...item,
                                    sell_price_sekunder: parseInt(
                                      numericValue.replace(/[^0-9]/g, ""),
                                      10
                                    ),
                                  });
                                }}
                                sx={{ marginBottom: 2 }}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      Rp.
                                    </InputAdornment>
                                  ),
                                }}
                              />
                              <TextField
                                id="outlined-basic"
                                fullWidth
                                label="Harga Jual Tersier"
                                autoComplete="off"
                                variant="outlined"
                                value={item.sell_price_tersier}
                                onChange={(e) => {
                                  let numericValue = e.target.value;
                                  if (numericValue === "") {
                                    numericValue = "0";
                                  }
                                  formik.setFieldValue(`product[${i}]`, {
                                    ...item,
                                    sell_price_tersier: parseInt(
                                      numericValue.replace(/[^0-9]/g, ""),
                                      10
                                    ),
                                  });
                                }}
                                sx={{ marginBottom: 2 }}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      Rp.
                                    </InputAdornment>
                                  ),
                                }}
                              />

                              <div>
                                <p
                                  className="text-blue-500 hover:underline hover:text-blue-500 cursor-pointer mb-2"
                                  onClick={() => {
                                    formik.setFieldValue(`product[${i}]`, {
                                      ...item,
                                      price_grosir: [
                                        ...item.price_grosir,
                                        {
                                          minimum: 0,
                                          maximum: 0,
                                          price_grosirs: 0,
                                        },
                                      ],
                                    });
                                  }}
                                >
                                  +Tambah Harga Grosir Baru
                                </p>
                              </div>

                              <div>
                                {item.price_grosir.length > 0 ? (
                                  <p className="font-bold my-2 w-full basis-1/3">
                                    Harga Grosir
                                  </p>
                                ) : null}
                                {item.price_grosir.map((gros, idx) => {
                                  return (
                                    <div
                                      className="flex justify-center items-center mb-2"
                                      key={idx}
                                    >
                                      <div className="w-full flex gap-2 items-center">
                                        <div className="w-full flex gap-2 items-center">
                                          <TextField
                                            fullWidth
                                            value={gros.minimum}
                                            onChange={(e) => {
                                              let numericValue = e.target.value;
                                              if (numericValue === "") {
                                                numericValue = "0";
                                              }
                                              formik.setFieldValue(
                                                `product[${i}]`,
                                                {
                                                  ...item,
                                                  price_grosir: [
                                                    ...item.price_grosir.slice(
                                                      0,
                                                      idx
                                                    ),
                                                    {
                                                      ...item.price_grosir[idx],
                                                      minimum: parseInt(
                                                        numericValue.replace(
                                                          /[^0-9]/g,
                                                          ""
                                                        ),
                                                        10
                                                      ),
                                                    },
                                                    ...item.price_grosir.slice(
                                                      idx + 1
                                                    ),
                                                  ],
                                                }
                                              );
                                            }}
                                          />
                                          <p>S/D</p>
                                          <TextField
                                            fullWidth
                                            value={gros.maximum}
                                            onChange={(e) => {
                                              let numericValue = e.target.value;
                                              if (numericValue === "") {
                                                numericValue = "0";
                                              }
                                              formik.setFieldValue(
                                                `product[${i}]`,
                                                {
                                                  ...item,
                                                  price_grosir: [
                                                    ...item.price_grosir.slice(
                                                      0,
                                                      idx
                                                    ),
                                                    {
                                                      ...item.price_grosir[idx],
                                                      maximum: parseInt(
                                                        numericValue.replace(
                                                          /[^0-9]/g,
                                                          ""
                                                        ),
                                                        10
                                                      ),
                                                    },
                                                    ...item.price_grosir.slice(
                                                      idx + 1
                                                    ),
                                                  ],
                                                }
                                              );
                                            }}
                                          />
                                        </div>
                                        <TextField
                                          fullWidth
                                          id="outlined-basic"
                                          variant="outlined"
                                          InputProps={{
                                            startAdornment: (
                                              <InputAdornment position="start">
                                                Rp.
                                              </InputAdornment>
                                            ),
                                          }}
                                          value={gros.price_grosirs}
                                          onChange={(e) => {
                                            let numericValue = e.target.value;
                                            if (numericValue === "") {
                                              numericValue = "0";
                                            }
                                            formik.setFieldValue(
                                              `product[${i}]`,
                                              {
                                                ...item,
                                                price_grosir: [
                                                  ...item.price_grosir.slice(
                                                    0,
                                                    idx
                                                  ),
                                                  {
                                                    ...item.price_grosir[idx],
                                                    price_grosirs: parseInt(
                                                      numericValue.replace(
                                                        /[^0-9]/g,
                                                        ""
                                                      ),
                                                      10
                                                    ),
                                                  },
                                                  ...item.price_grosir.slice(
                                                    idx + 1
                                                  ),
                                                ],
                                              }
                                            );
                                          }}
                                        />
                                        <button
                                          type="button"
                                          className="ml-[10px] text-red-500 hover:text-gray-300 "
                                          onClick={() => {
                                            formik.setFieldValue(
                                              `product[${i}]`,
                                              {
                                                ...item,
                                                price_grosir: [
                                                  ...item.price_grosir.slice(
                                                    0,
                                                    idx
                                                  ),
                                                  ...item.price_grosir.slice(
                                                    idx + 1
                                                  ),
                                                ],
                                              }
                                            );
                                          }}
                                        >
                                          <FaTrash />
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>

                              <div>
                                {platforms && platforms !== undefined ? (
                                  <>
                                    <p
                                      className="text-blue-500 text-endtext-end  hover:underline hover:text-blue-700 cursor-pointer"
                                      onClick={() => handleAddPlatform(item, i)}
                                    >
                                      +Tambah Platform lain
                                    </p>

                                    {item.platform_price.map((plat, idx) => {
                                      return (
                                        <div
                                          className="flex justify-center items-center mt-[20px]"
                                          key={idx}
                                        >
                                          <FormControl
                                            fullWidth
                                            sx={{ marginRight: 1 }}
                                          >
                                            <InputLabel id="demo-simple-select-label">
                                              Kategori
                                            </InputLabel>
                                            <Select
                                              id="add-category"
                                              label="Kategori"
                                              value={plat.platform_id}
                                              onChange={(e) => {
                                                formik.setFieldValue(
                                                  `product[${i}].platform_price[${idx}]`,
                                                  {
                                                    ...plat,
                                                    platform_id: e.target.value,
                                                  }
                                                );
                                              }}
                                            >
                                              {platforms.map((p) => {
                                                return (
                                                  <MenuItem
                                                    key={p.id}
                                                    value={p.id}
                                                    disabled={
                                                      // dataBuffer.map((u) =>
                                                      //   u.platform_price.find(
                                                      //     (f) =>
                                                      //       f.platform_id ===
                                                      //       p.id
                                                      //   )
                                                      // )[index]
                                                      //   ? true
                                                      //   : false
                                                      false
                                                    }
                                                    // sx={{
                                                    //   display: dataBuffer.map(
                                                    //     (u) =>
                                                    //       u.platform_price.find(
                                                    //         (f) =>
                                                    //           f.platform_id ===
                                                    //           p.id
                                                    //       )
                                                    //   )[index]
                                                    //     ? "none"
                                                    //     : null,
                                                    // }}
                                                  >
                                                    {p.name}
                                                  </MenuItem>
                                                );
                                              })}
                                            </Select>
                                          </FormControl>
                                          <TextField
                                            id="outlined-basic"
                                            fullWidth
                                            label="Harga Jual"
                                            autoComplete="off"
                                            variant="outlined"
                                            value={plat.price}
                                            onChange={(e) => {
                                              if (
                                                !isNaN(Number(e.target.value))
                                              )
                                                formik.setFieldValue(
                                                  `product[${i}].platform_price[${idx}]`,
                                                  {
                                                    ...plat,
                                                    price: Number(
                                                      e.target.value
                                                    ),
                                                  }
                                                );
                                            }}
                                            InputProps={{
                                              startAdornment: (
                                                <InputAdornment position="start">
                                                  Rp
                                                </InputAdornment>
                                              ),
                                              endAdornment: (
                                                <InputAdornment position="start">
                                                  <IconButton
                                                    aria-label="toggle password visibility"
                                                    edge="end"
                                                    onClick={() => {
                                                      formik.setFieldValue(
                                                        `product[${i}].platform_price`,
                                                        [
                                                          ...formik.values.product[
                                                            i
                                                          ].platform_price.slice(
                                                            0,
                                                            idx
                                                          ),
                                                          ...formik.values.product[
                                                            i
                                                          ].platform_price.slice(
                                                            idx + 1
                                                          ),
                                                        ]
                                                      );
                                                    }}
                                                  >
                                                    <FaTrash />
                                                  </IconButton>
                                                </InputAdornment>
                                              ),
                                            }}
                                          />
                                        </div>
                                      );
                                    })}
                                  </>
                                ) : null}
                              </div>
                            </div>
                          </Collapse>
                        </div>
                      );
                    })}
                  </>
                ) : (
                  <div className="flex items-center justify-center">
                    {isLoadingProduct ? (
                      <CircularProgress size={20} />
                    ) : (
                      "Data tidak ditemukan"
                    )}
                  </div>
                )}
              </div>

              <p className="mb-4 text-red-500">{errorMessage}</p>
            </div>

            {dataProduct && !isLoadingProduct ? (
              <div className=" flex gap-2 items-center px-8 pb-4">
                <button
                  type="button"
                  className="w-full p-2 rounded-md border-2 border-black hover:bg-gray-300"
                  disabled={isLoadingUpdateStock}
                  onClick={handleClose}
                >
                  Batal
                </button>
                <button
                  type="submit"
                  className="w-full p-2 rounded-md border-2 border-blue-500 bg-blue-500 text-white hover:bg-gray-300 hover:border-gray-300 disabled:bg-gray-300 disabled:border-none"
                  disabled={isLoadingUpdateStock}
                >
                  {isLoadingUpdateStock ? (
                    <CircularProgress size={15} />
                  ) : (
                    "Ubah"
                  )}
                </button>
              </div>
            ) : null}
          </form>
        </Box>
      </Modal>
    </>
  );
};

export default ModalChangePrice;
